/**
 * Title, H1, description and alt-text formulas (apps/web-app/plan.md,
 * "Title / H1 formulas" and "Alt text formulas"), and the complete head each
 * page type renders.
 *
 * Pure module (no `window`, no React): `<PageHead/>` renders a `SeoHead` in the
 * SPA, and the SEO server (plan W1) renders the same object into the HTML it
 * sends, so the two can never disagree.
 *
 * Rules that apply everywhere:
 * - "Today" and "{Mon YYYY}" appear only when at least one listed promotion is
 *   active now, in the locale's time zone.
 * - "Up to {X}%" and every other discount phrase come only from the backend's
 *   structured `discountType` / `discountMin` / `discountMax` (backend B3.5,
 *   `./offer`) and are omitted when those say nothing — never parsed from
 *   text; a phrase the headline already states is not repeated.
 * - Promotions are named by the backend's clean `headline` (B3.5), else their
 *   cleaned name.
 * - Names go through `cleanName()`; a brand is not repeated when the headline
 *   already starts with it; the site name is never repeated in a title.
 * - Descriptions are built from data, stripped of hashtags and URLs, and at
 *   most 160 characters.
 * - A staff-set `seoOverrides.title` / `.description` (backend B3.4) wins over
 *   the formula on brand, shop, store and promotion pages (and the hubs that
 *   pass them). Robots never come from overrides — whatever an override
 *   object carries — only from the backend's `seo.robots` (backend B1.4) or,
 *   until then, `./lifecycle`.
 *
 * The words come from the i18n catalogue (`@/i18n/locales/en.json`, one
 * namespace per page; web plan W3.0) through a `t` function: every builder
 * takes `options.t` (the SPA passes its locale route's, @/i18n/LocaleLanguage)
 * and defaults to `tFor(options.locale)`, the same bundled catalogue the SEO
 * server loads in Node. Dates in titles keep `./text`'s fixed English month
 * names; src/seo/templates.i18n.test.ts pins every output to what it was
 * before the switch.
 */
import { tFor, type Translate } from '@/i18n';
import { DEFAULT_LOCALE, LOCALES, localeDir, ogLocale, type Locale } from './locales';
import {
  hasActivePromotion,
  INDEX_ROBOTS,
  NOINDEX_ROBOTS,
  promotionLifecycle,
  promotionPhase,
  promotionWindow,
  robotsValue,
  type PromotionDates,
} from './lifecycle';
import {
  availabilityText,
  discountPhrase,
  statesDiscount,
  structuredDiscount,
  validInStores,
  type DiscountFields,
} from './offer';
import { saleEventYear, type SaleEvent } from './saleEvents';
import {
  buildBreadcrumbLd,
  buildSiteLd,
  hasBreadcrumbTrail,
  promotionMerchant,
  type Crumb,
} from './structuredData';
import {
  brandSeoName,
  brandTitleName,
  cityLabel,
  clampText,
  cleanAddress,
  cleanDescription,
  cleanName,
  formatDayMonth,
  formatDayMonthYear,
  formatLocalDayRange,
  formatMonthYear,
} from './text';
import {
  absoluteUrl,
  canonicalFor,
  catalogPath,
  entityPath,
  chooserLive,
  LOCALE_ROUTES_LIVE,
  localePath,
  SITE_URL,
  type EntityRef,
} from './urls';

export const SITE_NAME = 'PromoStork';
export const TITLE_SUFFIX = ` | ${SITE_NAME}`;

/** DEFAULT_LOCALE's catalogue, for the site defaults below. */
const defaultT = tFor(DEFAULT_LOCALE);

/**
 * The site defaults, identical to index.html's static head: the home head
 * without a live offer, `homeHead({ liveOffers: false })` — never "Today",
 * because a default cannot know what is live. Constants (DEFAULT_LOCALE's
 * catalogue strings, read once), so `<PageHead/>` and the SEO server's titled heads
 * (`./pageHeads`) share them without calling the formula;
 * src/seo/indexHtml.test.ts keeps index.html, these and the template in sync.
 */
export const DEFAULT_TITLE = defaultT('home:head.title', { site: SITE_NAME, country: DEFAULT_LOCALE.country });

export const DEFAULT_DESCRIPTION = defaultT('home:head.description', { country: DEFAULT_LOCALE.country });

/** Separator of a page-titled head: "{title} · PromoStork" (`<PageHead title>`). */
export const TITLED_SEPARATOR = ' · ';

/**
 * The site-wide share image, used when a page has none of its own: 1200×630,
 * ≤ 150 KB (web plan W0.4). index.html's static og:image / twitter:image point
 * at the same file; `public/og-image.png` is kept only for old cached shares.
 */
export const DEFAULT_OG_IMAGE_PATH = '/og-image.jpg';

/** Alt text of the default share image, which is the PromoStork logo ("{Brand} logo"), in `t`'s language. */
export function siteImageAlt(t: Translate = defaultT): string {
  return t('common:alt.logo', { name: SITE_NAME });
}

/** `siteImageAlt()` in DEFAULT_LOCALE's language. */
export const DEFAULT_OG_IMAGE_ALT = siteImageAlt();

/** Pixel size of the default share image (`og:image:width` / `og:image:height`). */
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;

/**
 * The size to declare for a share image: known only for the site image. Any
 * other image (a CDN crop of a cover, a poster) gets none, so a chat app
 * measures it instead of trusting a size that may be wrong.
 */
export function ogImageSize(imageUrl: string): { width: number; height: number } | null {
  return imageUrl === absoluteUrl(DEFAULT_OG_IMAGE_PATH)
    ? { width: DEFAULT_OG_IMAGE_WIDTH, height: DEFAULT_OG_IMAGE_HEIGHT }
    : null;
}

export const DESCRIPTION_MAX = 160;

/** A headline longer than this is cut (at a word) inside the `<title>`; the H1 keeps it whole. */
export const TITLE_HEADLINE_MAX = 70;

export type { Crumb };

/** One `<link rel="alternate" hreflang>`: a version of the page in another country (master D-11). */
export interface SeoAlternate {
  hreflang: string;
  /** Absolute, on SITE_URL. */
  url: string;
}

/** Everything a page's `<head>` needs, plus the H1 and the breadcrumb trail its body shows. */
export interface SeoHead {
  /** Complete `<title>`, site name included. */
  title: string;
  /** The page's one H1 (rendered in the body, not the head). */
  h1: string;
  /** Meta description, ≤ 160 characters. */
  description: string;
  /** Normalised canonical path (`?page=N` kept for N ≥ 2). */
  canonicalPath: string;
  /** Absolute canonical URL on SITE_URL; also `og:url`. */
  canonical: string;
  /** Exact robots meta value. */
  robots: string;
  /** Status the SEO server should send (the SPA can only mirror it with robots). */
  status: 200 | 410;
  /** `<html lang>`. */
  htmlLang: string;
  /** `<html dir="rtl">`: present only for a right-to-left language (web plan MW10); absent means left to right. */
  htmlDir?: 'rtl';
  /**
   * The page's other-country versions (and itself), for `rel="alternate"
   * hreflang`. Absent when the page exists in one country only, is paginated
   * past page 1 or is `noindex`.
   */
  alternates?: SeoAlternate[];
  og: {
    type: 'website';
    siteName: string;
    locale: string;
    title: string;
    description: string;
    url: string;
    image: string;
    imageAlt: string;
    /** Known pixel size for `image`; present when the size is certain (e.g. the backend share card or the site image). */
    imageSize: { width: number; height: number } | null;
  };
  twitter: {
    card: 'summary_large_image';
    title: string;
    description: string;
    image: string;
    imageAlt: string;
  };
  /** Home › … › this page. Feeds the BreadcrumbList and the visible breadcrumb (plan W0.8). */
  breadcrumbs: Crumb[];
  /** JSON-LD objects (without `@context`): the caller's entity data + the BreadcrumbList. */
  jsonLd: object[];
}

/** Options every head builder accepts. */
export interface HeadOptions {
  locale?: Locale;
  /** The catalogue's `t` for `locale`'s language; defaults to `tFor(locale)` (@/i18n). */
  t?: Translate;
  /** The moment "active now" is judged at. */
  now?: Date;
  /** 1-based page of a paginated list; ≥ 2 adds " – Page N" and `?page=N`. */
  page?: number;
  /** Absolute share image; defaults to the site image. */
  image?: string | null;
  /**
   * Known pixel dimensions for `image`. When provided, `og:image:width` /
   * `og:image:height` are emitted; otherwise `ogImageSize` decides (only the
   * static site image gets 1200×630 by default). Set to `{ width: 1200,
   * height: 630 }` when the image is a backend-composed share card (W3.9).
   */
  imageSize?: { width: number; height: number } | null;
  /** Alt text for `image` (see the alt formulas below); defaults to the title, or to DEFAULT_OG_IMAGE_ALT without an image. */
  imageAlt?: string | null;
  /** Entity JSON-LD from `./structuredData`. Do not include a BreadcrumbList: it is built from the crumbs. */
  jsonLd?: object | object[] | null;
  /** The page's hreflang pairs (see SeoHead.alternates); dropped for a `noindex` page. */
  alternates?: readonly SeoAlternate[] | null;
}

/** Staff overrides (backend B3.4) and the backend's computed SEO answer (B1.4). */
export interface SeoData {
  seoOverrides?: { title?: string | null; description?: string | null } | null;
  seo?: { robots?: string | null } | null;
}

/** Listed promotions, or an explicit answer, for the "Today" rule on list pages. */
export interface LiveOffersInput {
  /** Whether at least one listed promotion is active now; computed from `listed` when omitted. */
  liveOffers?: boolean;
  /** The promotions the page lists (dates are enough). */
  listed?: readonly PromotionDates[] | null;
}

// ─── Small helpers ─────────────────────────────────────────────────────────

/** "A", "A and B", "A, B and C". */
function joinAnd(items: readonly string[], t: Translate): string {
  if (items.length <= 1) return items[0] ?? '';
  return t('common:list.and', { head: items.slice(0, -1).join(', '), last: items[items.length - 1] });
}

/** Join sentences with ". ", without doubling the stop after "!", "?" or "…". */
function sentences(parts: readonly (string | null | undefined | false)[]): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => p.trim())
    .map((p) => (/[.!?…]$/.test(p) ? p : `${p}.`))
    .join(' ');
}

/** True when `text` starts with `prefix` as a whole word, ignoring case. */
function startsWithWord(text: string, prefix: string): boolean {
  if (!prefix || text.length < prefix.length) return false;
  if (text.slice(0, prefix.length).toLowerCase() !== prefix.toLowerCase()) return false;
  const next = text.charAt(prefix.length);
  return next === '' || !/[\p{L}\p{N}]/u.test(next);
}

function isLive(input: LiveOffersInput, now: Date, locale: Locale): boolean {
  return input.liveOffers ?? hasActivePromotion(input.listed, now, locale);
}

function validPage(page: number | undefined): number | null {
  return page !== undefined && Number.isSafeInteger(page) && page >= 2 ? page : null;
}

/**
 * "{base} – Page {n} | PromoStork": the suffix is added exactly once and stays
 * last. Only an existing " | PromoStork" suffix counts — data that merely
 * mentions the site ("PromoStork Exclusive: 10% Off") still gets it.
 */
export function withSiteName(base: string, page?: number, t: Translate = defaultT): string {
  const n = validPage(page);
  const core = base.endsWith(TITLE_SUFFIX) ? base.slice(0, -TITLE_SUFFIX.length) : base;
  return `${n ? t('common:title.page', { title: core, page: n }) : core}${TITLE_SUFFIX}`;
}

/**
 * Over this many characters (suffix included) a title is shown cut off by search
 * results; the site name, which they add themselves, is what goes (audit F12).
 */
export const TITLE_SOFT_MAX = 62;

/**
 * `withSiteName`, except that a title that would pass TITLE_SOFT_MAX with the
 * suffix goes without it: "Bata Pakistan Nawabshah: 3 Stores, Addresses &
 * Current Sale" keeps its brand and its city. For the long, name-driven titles
 * (brand × city); the fixed formulas keep the suffix.
 */
function titleWithinLimit(base: string, t: Translate): string {
  const full = withSiteName(base, undefined, t);
  return full.length > TITLE_SOFT_MAX ? full.slice(0, -TITLE_SUFFIX.length) : full;
}

/** Distinct, cleaned city names in first-seen order. */
function distinctCities(stores: readonly { city?: string | null }[] | null | undefined): string[] {
  const seen = new Map<string, string>();
  for (const store of stores ?? []) {
    const city = cleanName(store.city);
    if (city && !seen.has(city.toLowerCase())) seen.set(city.toLowerCase(), city);
  }
  return [...seen.values()];
}

/** "Lahore", "Lahore and Karachi", "Lahore, Karachi and 4 more cities". */
function cityList(cities: readonly string[], t: Translate): string {
  if (cities.length <= 3) return joinAnd(cities, t);
  return t('common:list.andMoreCities', { head: cities.slice(0, 2).join(', '), count: cities.length - 2 });
}

interface HeadParts {
  title: string;
  h1: string;
  description: string;
  path: string;
  robots: string;
  status?: 200 | 410;
  breadcrumbs: Crumb[];
  paginated?: boolean;
  /**
   * Every part of `description` is already clean (a `cleanAddress` whose "#"
   * unit numbers must survive): it is only put on one line and clamped, not
   * run through `cleanDescription` again, which would strip "#F14" as a hashtag.
   */
  descriptionIsClean?: boolean;
}

/** JSON-LD node types that describe the page itself (see `./structuredData`). */
const PAGE_NODE_TYPES: ReadonlySet<unknown> = new Set(['WebPage', 'CollectionPage']);

/**
 * A page node (`WebPage` / `CollectionPage`) is named by the page's H1 and
 * described by its meta description, unless its builder already set them, so
 * the structured data and the head never disagree. Other nodes pass through.
 */
function describePageNode(node: object, name: string, description: string): object {
  const fields = node as Record<string, unknown>;
  if (!PAGE_NODE_TYPES.has(fields['@type'])) return node;
  const described = { ...fields };
  if (described.name === undefined && name) described.name = name;
  if (described.description === undefined && description) described.description = description;
  return described;
}

/**
 * Page N ≥ 2 of a list: "{description} Page N." within `DESCRIPTION_MAX`, so
 * no two pages of one list share a meta description (page 1 is unchanged).
 */
function pagedDescription(description: string, page: number | null, t: Translate): string {
  if (!page || !description) return description;
  const suffix = t('common:descriptionPage', { page });
  return `${clampText(description, DESCRIPTION_MAX - suffix.length - 1)} ${suffix}`;
}

function buildHead(parts: HeadParts, options: HeadOptions, data: SeoData = {}): SeoHead {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const page = parts.paginated ? validPage(options.page) : null;

  const overrideTitle = data.seoOverrides?.title?.trim();
  const title = overrideTitle || parts.title;
  const description = pagedDescription(
    cleanDescription(data.seoOverrides?.description, { max: DESCRIPTION_MAX }) ||
      (parts.descriptionIsClean
        ? clampText(parts.description.replace(/\s+/g, ' ').trim(), DESCRIPTION_MAX)
        : cleanDescription(parts.description, { max: DESCRIPTION_MAX })),
    page,
    options.t ?? tFor(locale),
  );

  const canonicalPath = canonicalFor(page ? `${parts.path}?page=${page}` : parts.path);
  const canonical = absoluteUrl(canonicalPath);
  const robots = data.seo?.robots?.trim() || parts.robots;
  const image = options.image || absoluteUrl(DEFAULT_OG_IMAGE_PATH);
  // The alt describes the image: the page's own image defaults to the title,
  // the site image is always the logo.
  const imageAlt = options.imageAlt?.trim() || (options.image ? title : siteImageAlt(options.t ?? tFor(locale)));

  const entityLd = (options.jsonLd ? ([] as object[]).concat(options.jsonLd) : []).map((node) =>
    describePageNode(node, parts.h1, description),
  );
  // The BreadcrumbList exactly when the page shows the same trail (plan W0.8).
  const jsonLd = hasBreadcrumbTrail(parts.breadcrumbs)
    ? [...entityLd, buildBreadcrumbLd(parts.breadcrumbs)]
    : entityLd;

  // Use an explicit imageSize when the caller knows it (e.g. backend share card
  // at 1200×630); otherwise fall back to the URL-based check (only the static
  // site image resolves to a known size today).
  const imageSize: { width: number; height: number } | null =
    options.imageSize ?? ogImageSize(image);

  // hreflang pairs only for an indexable, first page, and only when there are two or more.
  const alternates =
    robots.startsWith('noindex') || page !== null || (options.alternates?.length ?? 0) < 2
      ? []
      : [...(options.alternates ?? [])];

  return {
    title,
    h1: parts.h1,
    description,
    canonicalPath,
    canonical,
    robots,
    status: parts.status ?? 200,
    htmlLang: locale.hreflang,
    ...(localeDir(locale) === 'rtl' ? { htmlDir: 'rtl' as const } : {}),
    ...(alternates.length ? { alternates } : {}),
    og: {
      type: 'website',
      siteName: SITE_NAME,
      locale: ogLocale(locale),
      title,
      description,
      url: canonical,
      image,
      imageAlt,
      imageSize,
    },
    twitter: { card: 'summary_large_image', title, description, image, imageAlt },
    breadcrumbs: parts.breadcrumbs,
    jsonLd,
  };
}

/**
 * The hreflang pairs of a catalog page that exists in every launched locale
 * (home, `/brands`, `/promotions`): each locale's own URL of `path`, itself
 * included (the answer is the same from each of them). Empty while the catalog
 * lives un-prefixed (before the URL move: one URL for every locale).
 */
export function localeAlternates(path: string): SeoAlternate[] {
  // Before the URL move there is one un-prefixed catalog, whatever the locale.
  if (!LOCALE_ROUTES_LIVE) return [];
  const alternates = LOCALES.map((locale) => ({
    hreflang: locale.hreflang,
    url: absoluteUrl(canonicalFor(catalogPath(locale, path))),
  }));
  if (new Set(alternates.map((alternate) => alternate.url)).size < 2) return [];
  // With the country chooser live (web plan MW10), `/` is the x-default of the locale homes.
  if (path === '/' && chooserLive()) alternates.push({ hreflang: 'x-default', url: absoluteUrl('/') });
  return alternates;
}

/**
 * The backend's `seo.alternates` of an entity page (every launched country it
 * shows in) as head alternates on SITE_URL: only the path of each is kept, so
 * the host is the one the canonical uses. Dropped unless it lists two or more
 * different URLs.
 */
export function entityAlternates(
  seo: { alternates?: ReadonlyArray<{ hreflang: string; url: string }> | null } | null | undefined,
): SeoAlternate[] {
  const alternates: SeoAlternate[] = [];
  for (const { hreflang, url } of seo?.alternates ?? []) {
    let path: string;
    try {
      path = new URL(url, SITE_URL).pathname;
    } catch {
      continue;
    }
    if (hreflang && path.startsWith('/') && !path.startsWith('//')) {
      alternates.push({ hreflang, url: absoluteUrl(canonicalFor(path)) });
    }
  }
  return new Set(alternates.map((alternate) => alternate.url)).size >= 2 ? alternates : [];
}

function ctx(options: HeadOptions) {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const now = options.now ?? new Date();
  const t = options.t ?? tFor(locale);
  return {
    locale,
    now,
    t,
    country: locale.country,
    tz: locale.timeZone,
    home: { name: t('common:crumb.home'), path: catalogPath(locale, '/') } satisfies Crumb,
  };
}

// ─── Home and lists ────────────────────────────────────────────────────────

/** The home description; "today" only with a live offer. */
function homeDescription(t: Translate, country: string, live: boolean): string {
  return t(live ? 'home:head.descriptionLive' : 'home:head.description', { country });
}

/**
 * The site defaults in `t`'s language for `locale` (DEFAULT_TITLE,
 * DEFAULT_DESCRIPTION and DEFAULT_OG_IMAGE_ALT are these for DEFAULT_LOCALE):
 * what `<PageHead/>` falls back to, as `titledHead` does on the server.
 */
export function siteDefaults(options: Pick<HeadOptions, 'locale' | 't'> = {}): {
  title: string;
  description: string;
  imageAlt: string;
} {
  const { t, country } = ctx(options);
  return {
    title: t('home:head.title', { site: SITE_NAME, country }),
    description: homeDescription(t, country, false),
    imageAlt: siteImageAlt(t),
  };
}

/**
 * Home `/` (then `/pk/en`): "PromoStork – Sale on Brands in {Country} Today,
 * Deals & Offers". Its JSON-LD is the site's `Organization` + `WebSite`, which
 * appear on the home page only (master D-8); the Organization is described by
 * the no-live description, which does not change with the offers.
 */
export function homeHead(input: LiveOffersInput, options: HeadOptions = {}): SeoHead {
  const { locale, now, t, country } = ctx(options);
  const live = isLive(input, now, locale);
  // Another country's home (and every home once `/` is the chooser, which owns the
  // site-wide ids) carries its own `@id`s: one `@id`, one set of values (audit F10).
  const scoped = locale.cc !== DEFAULT_LOCALE.cc || chooserLive();
  const siteLd = buildSiteLd({ name: SITE_NAME, description: homeDescription(t, country, false), locale, scoped });
  return buildHead(
    {
      title: t(live ? 'home:head.titleLive' : 'home:head.title', { site: SITE_NAME, country }),
      h1: t('home:head.h1', { country }),
      description: homeDescription(t, country, live),
      path: catalogPath(locale, '/'),
      robots: INDEX_ROBOTS,
      breadcrumbs: [],
    },
    {
      ...options,
      alternates: options.alternates ?? localeAlternates('/'),
      jsonLd: [...siteLd, ...(options.jsonLd ? ([] as object[]).concat(options.jsonLd) : [])],
    },
  );
}

/**
 * The country chooser at `/` (web plan MW10): the page every launched country's
 * home names as its `x-default`. Carries the same `Organization` + `WebSite`
 * JSON-LD as the locale homes (master D-8) and lists no offers, so it is data
 * free. Its words are the default locale's (it belongs to no country).
 */
export function chooserHead(options: HeadOptions = {}): SeoHead {
  const { locale, t } = ctx({ ...options, locale: options.locale ?? DEFAULT_LOCALE });
  const countries = joinAnd(
    LOCALES.map((launched) => launched.country),
    t,
  );
  // Belongs to no country: the nodes without the locale's `areaServed` / `inLanguage`.
  const siteLd = buildSiteLd({
    name: SITE_NAME,
    description: t('chooser:head.description', { site: SITE_NAME, countries }),
    locale,
  }).map((node) => {
    const { areaServed: _area, inLanguage: _language, ...rest } = node as Record<string, unknown>;
    return rest;
  });
  return buildHead(
    {
      title: t('chooser:head.title', { site: SITE_NAME }),
      h1: t('chooser:head.h1'),
      description: t('chooser:head.description', { site: SITE_NAME, countries }),
      path: '/',
      robots: INDEX_ROBOTS,
      breadcrumbs: [],
    },
    {
      ...options,
      locale,
      t,
      alternates: [
        ...LOCALES.map((launched) => ({
          hreflang: launched.hreflang,
          url: absoluteUrl(canonicalFor(localePath(launched, '/'))),
        })),
        { hreflang: 'x-default', url: absoluteUrl('/') },
      ],
      jsonLd: [...siteLd, ...(options.jsonLd ? ([] as object[]).concat(options.jsonLd) : [])],
    },
  );
}

/** `…/promotions`: "All Current Sales & Offers in {Country} ({Mon YYYY}) | PromoStork". */
export function promotionsListHead(
  input: LiveOffersInput & { totalCount?: number | null; noindex?: boolean } = {},
  options: HeadOptions = {},
): SeoHead {
  const { locale, now, t, country, tz, home } = ctx(options);
  const live = isLive(input, now, locale);
  const monthYear = live ? formatMonthYear(now, tz) : '';
  const path = catalogPath(locale, '/promotions');
  const count = input.totalCount && input.totalCount > 0 ? input.totalCount : null;
  const what = count ? t('promotions:head.offerCount', { count }) : t('promotions:head.offers');
  return buildHead(
    {
      title: withSiteName(
        t(monthYear ? 'promotions:head.titleLive' : 'promotions:head.title', { country, monthYear }),
        options.page,
        t,
      ),
      h1: t('promotions:head.h1', { country }),
      description: sentences([
        t(monthYear ? 'promotions:head.summaryLive' : 'promotions:head.summary', { what, country, monthYear }),
        t('promotions:head.browse'),
      ]),
      path,
      robots: input.noindex ? NOINDEX_ROBOTS : INDEX_ROBOTS,
      breadcrumbs: [home, { name: t('common:crumb.promotions'), path }],
      paginated: true,
    },
    { ...options, alternates: options.alternates ?? localeAlternates('/promotions') },
  );
}

/** `…/brands`: "Brands in {Country}: Clothing & Shoe Brands with Live Sales | PromoStork". */
export function brandsListHead(
  input: { totalCount?: number | null } = {},
  options: HeadOptions = {},
): SeoHead {
  const { locale, t, country, home } = ctx(options);
  const path = catalogPath(locale, '/brands');
  const count = input.totalCount && input.totalCount > 0 ? input.totalCount : null;
  const what = count ? t('common:count.brand', { count }) : t('brands:head.brands');
  return buildHead(
    {
      title: withSiteName(t('brands:head.title', { country }), options.page, t),
      h1: t('brands:head.h1', { country }),
      description: sentences([t('brands:head.summary', { what, country }), t('brands:head.browse')]),
      path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, { name: t('common:crumb.brands'), path }],
      paginated: true,
    },
    { ...options, alternates: options.alternates ?? localeAlternates('/brands') },
  );
}

/** `…/shops`: "Shops in {Country}: Deals & Live Sales | PromoStork". */
export function shopsListHead(
  input: { totalCount?: number | null; noindex?: boolean } = {},
  options: HeadOptions = {},
): SeoHead {
  const { locale, t, country } = ctx(options);
  const path = catalogPath(locale, '/shops');
  const count = input.totalCount && input.totalCount > 0 ? input.totalCount : null;
  const what = count ? t('common:count.shop', { count }) : t('shops:head.shops');
  return buildHead(
    {
      title: withSiteName(t('shops:head.listTitle', { country }), options.page, t),
      h1: t('shops:head.h1', { country }),
      description: sentences([t('shops:head.summary', { what, country }), t('shops:head.browse')]),
      path,
      robots: input.noindex ? NOINDEX_ROBOTS : INDEX_ROBOTS,
      // No trail: the page shows none (web plan W0.8), so no BreadcrumbList either.
      breadcrumbs: [],
      paginated: true,
    },
    options,
  );
}

/** `…/nearby`: "Sales & Deals Near Me in {Country} | PromoStork". */
export function nearbyHead(options: HeadOptions = {}): SeoHead {
  const { locale, t, country, home } = ctx(options);
  const path = catalogPath(locale, '/nearby');
  const h1 = t('nearby:head.h1');
  return buildHead(
    {
      title: withSiteName(t('nearby:head.title', { country }), undefined, t),
      h1,
      description: t('nearby:head.description', { country }),
      path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, { name: h1, path }],
    },
    options,
  );
}

// ─── Brands and shops ──────────────────────────────────────────────────────

/** A listed promotion as far as brand and hub titles care: its dates and structured discount (B3.5). */
export interface ListedPromotion extends PromotionDates, DiscountFields {}

export interface BrandSeoInput extends EntityRef, SeoData {
  name: string;
  /** Short name for titles and H1s (backend SPB5); `name` while empty. */
  seoName?: string | null;
  /** The brand's listed promotions (dates and the structured discount). */
  promotions?: readonly ListedPromotion[] | null;
  /** Its stores (for "N stores in M cities"). */
  stores?: readonly { city?: string | null }[] | null;
  /** Exact counts when the page has them (e.g. from the backend); override `stores`. */
  storeCount?: number | null;
  cityCount?: number | null;
}

/**
 * The biggest structured percentage off among active promotions (FLAT and
 * UP_TO; a cashback or a BOGO is not "% off", `./offer`), and that
 * promotion's end.
 */
function liveDeal(
  promos: readonly ListedPromotion[],
  now: Date,
  locale: Locale,
): { max: number | null; end: Date | null } {
  let best: { max: number | null; end: Date | null } = { max: null, end: null };
  for (const promo of promos) {
    if (promotionPhase(promo, now, locale) !== 'active') continue;
    const max = structuredDiscount(promo)?.percentOff ?? null;
    const { end } = promotionWindow(promo, locale);
    const better =
      (max ?? -1) > (best.max ?? -1) ||
      ((max ?? -1) === (best.max ?? -1) && (end?.getTime() ?? 0) > (best.end?.getTime() ?? 0));
    if (better) best = { max, end };
  }
  return best;
}

function hubHead(
  kind: 'brand' | 'shop',
  entity: BrandSeoInput,
  options: HeadOptions,
): SeoHead {
  const { locale, now, t, country, tz, home } = ctx(options);
  const name = brandSeoName(entity);
  // Titles only: without a registered name's legal form (`brandTitleName`).
  const titleName = brandTitleName(entity);
  const promos = entity.promotions ?? [];
  const live = hasActivePromotion(promos, now, locale);
  const deal = liveDeal(promos, now, locale);
  const path = entityPath(entity, kind);

  const cities = distinctCities(entity.stores);
  const storeCount = entity.storeCount ?? entity.stores?.length ?? 0;
  const cityCount = entity.cityCount ?? cities.length;
  const stores = storeCount > 0 ? t('common:count.namedStore', { count: storeCount, name }) : '';
  const where =
    stores && cityCount > 0
      ? t('brand:head.storesInCities', { stores, cities: t('common:count.city', { count: cityCount }) })
      : stores;
  const liveCount = promos.filter((p) => promotionPhase(p, now, locale) === 'active').length;

  // "{Brand} sale is live[: up to {X}% off][ until {D Mon YYYY}]".
  const liveKey = deal.max ? (deal.end ? 'liveUpToUntil' : 'liveUpTo') : deal.end ? 'liveUntil' : 'live';
  const description = live
    ? sentences([
        t(`brand:head.${liveKey}`, {
          name,
          max: deal.max ?? '',
          date: deal.end ? formatDayMonthYear(deal.end, tz) : '',
        }),
        t(where ? 'brand:head.seeOffersWhere' : 'brand:head.seeOffers', { count: liveCount, where, name, country }),
      ])
    : sentences([
        t('brand:head.notLive', { name }),
        t(where ? 'brand:head.seePastWhere' : 'brand:head.seePast', { name, where }),
      ]);

  // A shop uses the brand hub's formulas (see `shopHead`); only its list differs.
  const list =
    kind === 'brand'
      ? { name: t('common:crumb.brands'), path: catalogPath(locale, '/brands') }
      : { name: t('common:crumb.shops'), path: catalogPath(locale, '/shops') };

  return buildHead(
    {
      title: withSiteName(
        live
          ? t(deal.max ? 'brand:head.titleLiveUpTo' : 'brand:head.titleLive', {
              name: titleName,
              max: deal.max ?? '',
              monthYear: formatMonthYear(now, tz),
            })
          : t('brand:head.title', { name: titleName }),
        undefined,
        t,
      ),
      h1: t('brand:head.h1', { name }),
      description,
      path,
      // D-5 indexes a hub with ≥ 1 store or ≥ 1 promotion EVER; only the
      // backend knows "ever", so its seo.robots decides once it exists.
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, list, { name, path }],
    },
    options,
    entity,
  );
}

/** Brand hub: "{Brand} Sale Today: Up to {X}% Off ({Mon YYYY})" or "{Brand} Sale: Past Offers, Next Sale & Stores". */
export function brandHead(brand: BrandSeoInput, options: HeadOptions = {}): SeoHead {
  return hubHead('brand', brand, options);
}

/**
 * Shop page. The plan's table has no shop row; a shop follows the brand hub's
 * index rule (master D-5), so it uses the brand formulas with its own name.
 */
export function shopHead(shop: BrandSeoInput, options: HeadOptions = {}): SeoHead {
  return hubHead('shop', shop, options);
}

// ─── Promotions ────────────────────────────────────────────────────────────

export interface PromotionSeoInput extends EntityRef, PromotionDates, SeoData, DiscountFields {
  name: string;
  /** The backend's clean headline (B3.5); falls back to `cleanName(name)`. */
  headline?: string | null;
  description?: string | null;
  /** Where it can be used (B3.5); an online-only offer names no stores. */
  availabilityScope?: string | null;
  brand?: (EntityRef & { name: string; seoName?: string | null }) | null;
  /**
   * Participating shops. A promotion can run at shops without a brand (the
   * backend allows it); its first named shop is then the merchant.
   */
  shops?: readonly (EntityRef & { name: string })[] | null;
  stores?: readonly { city?: string | null }[] | null;
}

// Who runs the promotion (its brand, or else its first named shop):
// `promotionMerchant` in ./structuredData, shared with the Offer's `offeredBy`.

/**
 * The promotion's clean headline, the merchant's name (`brand`: the brand, or
 * for a shop-only promotion its first shop) and whether it must prefix the
 * headline.
 */
function promotionNames(promo: PromotionSeoInput): { brand: string; headline: string; prefixBrand: boolean } {
  const brand = promotionMerchant(promo)?.name ?? '';
  const headline = cleanName(promo.headline) || cleanName(promo.name);
  return { brand, headline, prefixBrand: Boolean(brand) && !startsWithWord(headline, brand) };
}

/** "Urbansole: Up To 50% Off" (H1); the brand is not repeated when the headline starts with it. */
export function promotionH1(promo: PromotionSeoInput, options: Pick<HeadOptions, 'locale' | 't'> = {}): string {
  const { t } = ctx(options);
  const { brand, headline, prefixBrand } = promotionNames(promo);
  if (!headline) return brand ? t('promotion:head.brandSale', { brand }) : t('promotion:head.fallback');
  return prefixBrand ? t('promotion:head.h1', { brand, headline }) : headline;
}

/**
 * "{Brand} Sale: {Clean headline} (Ends {D Mon}) | PromoStork", with
 * "(Starts {D Mon})" for an upcoming and "(Ended {D Mon YYYY})" for an ended
 * promotion; no date part for an active promotion without an end date.
 */
export function promotionTitle(
  promo: PromotionSeoInput,
  options: Pick<HeadOptions, 'now' | 'locale' | 't'> = {},
): string {
  const { locale, now, t, tz } = ctx(options);
  const { brand, headline, prefixBrand } = promotionNames(promo);
  const short = clampText(headline, TITLE_HEADLINE_MAX);
  const base = !short
    ? brand
      ? t('promotion:head.brandSale', { brand })
      : t('promotion:head.fallback')
    : prefixBrand
      ? t('promotion:head.title', { brand, headline: short })
      : short;

  const phase = promotionPhase(promo, now, locale);
  const { start, end } = promotionWindow(promo, locale);
  let title = base;
  if (phase === 'upcoming') title = t('promotion:head.titleStarts', { title: base, date: formatDayMonth(start, tz) });
  else if (phase === 'ended') title = t('promotion:head.titleEnded', { title: base, date: formatDayMonthYear(end, tz) });
  else if (end) title = t('promotion:head.titleEnds', { title: base, date: formatDayMonth(end, tz) });
  return withSiteName(title, undefined, t);
}

/** A promotion page: title, H1, data-built description, and D-4 robots/status. */
export function promotionHead(promo: PromotionSeoInput, options: HeadOptions = {}): SeoHead {
  const { locale, now, t, tz, home } = ctx(options);
  const lifecycle = promotionLifecycle(promo, now, locale);
  const { headline } = promotionNames(promo);
  const h1 = promotionH1(promo, { locale, t });
  const path = entityPath(promo, 'promotion');

  const dates = promotionWindow(promo, locale);
  const start = formatDayMonthYear(dates.start, tz);
  const end = formatDayMonthYear(dates.end, tz);
  const when =
    lifecycle.phase === 'upcoming'
      ? t(end ? 'promotion:head.startsEnds' : 'promotion:head.starts', { start, end })
      : lifecycle.phase === 'ended'
        ? t('promotion:head.ended', { end })
        : end
          ? t('promotion:head.ends', { end })
          : '';
  // "Up to 50% off", "Flat 30% off", "Buy 1 get 1 free"… from the structured
  // fields only, and not when the headline already says it ("Up To 50% Off").
  const discount = statesDiscount(headline, promo, t) ? '' : discountPhrase(promo, t);
  const cities = distinctCities(promo.stores);
  const storeCount = promo.stores?.length ?? 0;
  const stores = storeCount > 0 ? t('common:count.store', { count: storeCount }) : '';
  // An online-only offer (B3.5 availabilityScope) says so instead of naming stores.
  const where = !validInStores(promo.availabilityScope)
    ? availabilityText(promo.availabilityScope, promotionMerchant(promo)?.name, t)
    : !stores
      ? ''
      : cities.length
        ? t('promotion:head.atStoresIn', { stores, cities: cityList(cities, t) })
        : t('promotion:head.atStores', { stores });

  const own = cleanDescription(promo.description, { max: Infinity });
  const description = [sentences([h1, when, discount, where]), own].filter(Boolean).join(' ');

  // Home › Brands › {Brand} › offer, or Home › Shops › {Shop} › offer for a
  // shop-only promotion; a shop is never linked under /brand/.
  const merchant = promotionMerchant(promo);
  const breadcrumbs = merchant
    ? [
        home,
        merchant.type === 'brand'
          ? { name: t('common:crumb.brands'), path: catalogPath(locale, '/brands') }
          : { name: t('common:crumb.shops'), path: catalogPath(locale, '/shops') },
        { name: merchant.name, path: entityPath(merchant.ref, merchant.type) },
        { name: headline || h1, path },
      ]
    : [home, { name: t('common:crumb.promotions'), path: catalogPath(locale, '/promotions') }, { name: h1, path }];

  return buildHead(
    {
      title: promotionTitle(promo, { ...options, t }),
      h1,
      description,
      path,
      robots: lifecycle.robots,
      status: lifecycle.status,
      breadcrumbs,
    },
    options,
    promo,
  );
}

// ─── Stores ────────────────────────────────────────────────────────────────

export interface StoreSeoInput extends EntityRef, SeoData {
  name: string;
  /** Neighbourhood / market (backend B3.2); omitted until it exists. */
  area?: string | null;
  city?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  mobileNumber?: string | null;
  brand?: (EntityRef & { name: string; seoName?: string | null }) | null;
}

// The city as `cityLabel` shows it: imported rows often type it all lowercase
// ("lahore"), which would otherwise reach the H1 and title as "Mocciani –
// lahore" (web plan W0.10; same rule as the brand page's city groups).
function storePlace(store: StoreSeoInput): { brand: string; place: string } {
  const brand = brandSeoName(store.brand) || cleanName(store.name);
  const place = [cleanName(store.area), cityLabel(store.city)].filter(Boolean).join(', ');
  return { brand, place };
}

/** The store's display name, as its H1 reads: "Mocciani – DHA Phase 6, Lahore" (the JSON-LD `name` is the same text). */
export function storeDisplayName(store: StoreSeoInput, options: Pick<HeadOptions, 'locale' | 't'> = {}): string {
  const { t } = ctx(options);
  const { brand, place } = storePlace(store);
  return place ? t('store:head.h1', { brand, place }) : brand;
}

/**
 * Store page: "{Brand} {Area}, {City} – Address, Phone & Offers | PromoStork".
 * `noindex, follow` in every state until the store clean-up (master D-5,
 * backend B3.2) unless the backend's `seo.robots` says otherwise.
 */
export function storeHead(store: StoreSeoInput, options: HeadOptions = {}): SeoHead {
  const { locale, t, home } = ctx(options);
  const { brand, place } = storePlace(store);
  const h1 = storeDisplayName(store, options);
  const path = entityPath(store, 'store');
  // "Shop #F14, Dolmen Mall": the same text as the page and the JSON-LD
  // PostalAddress (`cleanAddress` keeps "#"); the phone gets the same clean-up.
  const address = cleanAddress(store.address);
  const phone = cleanAddress(store.phoneNumber || store.mobileNumber);
  const city = cityLabel(store.city);

  const breadcrumbs = store.brand
    ? [
        home,
        { name: t('common:crumb.brands'), path: catalogPath(locale, '/brands') },
        { name: brandSeoName(store.brand) || brand, path: entityPath(store.brand, 'brand') },
        { name: h1, path },
      ]
    : [home, { name: h1, path }];

  return buildHead(
    {
      title: withSiteName(
        place ? t('store:head.title', { brand, place }) : t('store:head.titleNoPlace', { brand }),
        undefined,
        t,
      ),
      h1,
      description: sentences([
        h1,
        address && t('store:head.address', { address }),
        phone && t('store:head.phone', { phone }),
        t(city ? 'store:head.offersIn' : 'store:head.offers', { brand, city }),
      ]),
      descriptionIsClean: true,
      path,
      robots: NOINDEX_ROBOTS,
      breadcrumbs,
    },
    options,
    store,
  );
}

// ─── Hubs (plan W3; heads for pages the backend's seo.robots gates) ──────────

/** Brand × city: "{Brand} {City}: {n} Stores, Addresses & Current Sale | PromoStork". */
export function brandCityHead(
  input: SeoData & {
    brand: EntityRef & { name: string; seoName?: string | null };
    city: string;
    storeCount: number;
    /** The page's path (the backend's `urlPath`). */
    path: string;
  },
  options: HeadOptions = {},
): SeoHead {
  const { locale, t, home } = ctx(options);
  const brand = brandSeoName(input.brand);
  const city = cleanName(input.city);
  const h1 = t('brandCity:head.h1', { brand, city });
  const stores = t('brandCity:head.storeCount', { count: input.storeCount });
  return buildHead(
    {
      title: titleWithinLimit(t('brandCity:head.title', { brand: brandTitleName(input.brand), city, stores }), t),
      h1,
      description: sentences([
        t('brandCity:head.summary', {
          stores: t('common:count.namedStore', { count: input.storeCount, name: brand }),
          city,
        }),
        t('brandCity:head.addresses', { brand }),
      ]),
      path: input.path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [
        home,
        { name: t('common:crumb.brands'), path: catalogPath(locale, '/brands') },
        { name: brand, path: entityPath(input.brand, 'brand') },
        { name: h1, path: input.path },
      ],
    },
    options,
    input,
  );
}

/** City hub: "Sale on Brands in {City} Today ({Mon YYYY})" or "…: Latest & Upcoming Offers, Stores". */
export function cityHead(
  input: LiveOffersInput & SeoData & { city: string; path: string; brandCount?: number | null },
  options: HeadOptions = {},
): SeoHead {
  const { locale, now, t, tz, home } = ctx(options);
  const city = cleanName(input.city);
  const live = isLive(input, now, locale);
  const h1 = t('city:head.h1', { city });
  const brands =
    input.brandCount && input.brandCount > 0
      ? t('common:count.brand', { count: input.brandCount })
      : t('city:head.brands');
  const monthYear = formatMonthYear(now, tz);
  return buildHead(
    {
      // " – Page N" on page N ≥ 2 of its live sales (web plan W3.2).
      title: withSiteName(t(live ? 'city:head.titleLive' : 'city:head.title', { city, monthYear }), options.page, t),
      h1,
      description: sentences([
        t(live ? 'city:head.summaryLive' : 'city:head.summary', { brands, city, monthYear }),
        t('city:head.latest'),
      ]),
      path: input.path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, { name: city, path: input.path }],
      // The hub's live sales page with `?page=N` (web plan W3.2).
      paginated: true,
    },
    options,
    input,
  );
}

/** Category hub: "{Category label} in {Country} Today: {Top 3 brands}" or "…: Brands, Latest & Upcoming Offers". */
export function categoryHead(
  input: LiveOffersInput &
    SeoData & {
      /** The category's SEO label (backend B3.4 `seoLabel`) or its title. */
      label: string;
      path: string;
      /** Brands with the most live offers in the category, best first. */
      topBrands?: readonly string[] | null;
      /** Ancestor categories, root first. */
      parents?: readonly Crumb[] | null;
    },
  options: HeadOptions = {},
): SeoHead {
  const { locale, now, t, country, home } = ctx(options);
  const label = cleanName(input.label);
  const live = isLive(input, now, locale);
  const top = (input.topBrands ?? []).map(cleanName).filter(Boolean).slice(0, 3);
  const h1 = t('category:head.h1', { label, country });
  const title = !live
    ? t('category:head.title', { label, country })
    : top.length
      ? t('category:head.titleLiveTop', { label, country, top: top.join(', ') })
      : t('category:head.titleLive', { label, country });
  return buildHead(
    {
      // " – Page N" on page N ≥ 2 of its live sales (web plan W3.1).
      title: withSiteName(title, options.page, t),
      h1,
      description: sentences([
        live && top.length
          ? t('category:head.summaryTop', { label, country, top: joinAnd(top, t) })
          : t('category:head.summary', { label, country }),
        t('category:head.latest'),
      ]),
      path: input.path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, ...(input.parents ?? []), { name: label, path: input.path }],
      // The hub's live sales page with `?page=N` (web plan W3.1).
      paginated: true,
    },
    options,
    input,
  );
}

// ─── Sale events ───────────────────────────────────────────────────────────

/** "Wed 11 Nov 2026", or "8 Feb – 9 Mar 2027 (expected, subject to moon sighting)". */
export function saleEventDates(
  event: Pick<SaleEvent, 'startDate' | 'endDate' | 'expected'>,
  options: Pick<HeadOptions, 'locale' | 't'> = {},
): string {
  const range = formatLocalDayRange(event.startDate, event.endDate);
  return event.expected ? ctx(options).t('saleEvent:dates.expected', { dates: range }) : range;
}

/** "11.11 Sale 2026": an event's name in titles, the calendar's list and breadcrumbs. */
export function saleEventName(
  event: Pick<SaleEvent, 'name' | 'startDate'>,
  options: Pick<HeadOptions, 'locale' | 't'> = {},
): string {
  return ctx(options).t('saleEvent:head.name', { event: event.name, year: saleEventYear(event) });
}

/** Sale event hub: "{Event} Sale {YYYY} in {Country}: Brands, Dates & Offers | PromoStork". */
export function saleEventHead(event: SaleEvent, options: HeadOptions = {}): SeoHead {
  const { locale, t, country, home } = ctx(options);
  const name = saleEventName(event, { locale, t });
  const path = localePath(locale, `/sale/${event.slug}`);
  return buildHead(
    {
      title: withSiteName(t('saleEvent:head.title', { name, country }), undefined, t),
      h1: event.aliases.length
        ? t('saleEvent:head.h1Aliases', { name, aliases: event.aliases.join(' / '), country })
        : t('saleEvent:head.h1', { name, country }),
      description: sentences([
        t('saleEvent:head.summary', { name, country, dates: saleEventDates(event, { locale, t }) }),
        event.aliases.length
          ? t('saleEvent:head.offersAliases', { event: event.name, aliases: event.aliases.join(', ') })
          : t('saleEvent:head.offers', { event: event.name }),
      ]),
      path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [
        home,
        { name: t('common:crumb.saleCalendar'), path: localePath(locale, '/sales') },
        { name, path },
      ],
    },
    // The event's own share artwork when it has one; the prerendered head
    // (plan W0.18) carries the same image.
    { ...options, image: options.image || (event.ogImage ? absoluteUrl(event.ogImage) : null) },
  );
}

/**
 * Sale calendar `/{cc}/{lang}/sales`. Not in the plan's title table: built
 * from the same vocabulary ("Sale Calendar", master D-2) and the event list.
 */
export function saleCalendarHead(events: readonly SaleEvent[], options: HeadOptions = {}): SeoHead {
  const { locale, t, country, home } = ctx(options);
  const path = localePath(locale, '/sales');
  const allNames = events.map((e) => e.name);
  // "A, B & C" for any count (one name stands alone).
  const joinNames = (names: readonly string[]): string =>
    names.length > 1
      ? t('saleCalendar:head.names', { head: names.slice(0, -1).join(', '), last: names[names.length - 1] })
      : (names[0] ?? '');
  // The title names every published event while its headline fits
  // TITLE_HEADLINE_MAX; with many events it names the longest run of them
  // that fits (never fewer than one), the description carries the rest.
  const titleFor = (names: readonly string[]) => t('saleCalendar:head.titleNames', { country, names: joinNames(names) });
  let titleNames = allNames;
  while (titleNames.length > 1 && titleFor(titleNames).length > TITLE_HEADLINE_MAX) {
    titleNames = titleNames.slice(0, -1);
  }
  const namesText = joinNames(titleNames);
  const datedEvents = events
    .map((e) => t('saleCalendar:head.eventOn', { event: e.name, dates: formatLocalDayRange(e.startDate, e.endDate) }))
    .join(', ');
  // The dated list while the description holds it; else every name, undated.
  const datedSummary = t('saleCalendar:head.summaryEvents', { country, events: datedEvents });
  const eventDates = datedSummary.length <= DESCRIPTION_MAX ? datedEvents : joinNames(allNames);
  return buildHead(
    {
      title: withSiteName(
        namesText
          ? t('saleCalendar:head.titleNames', { country, names: namesText })
          : t('saleCalendar:head.title', { country }),
        undefined,
        t,
      ),
      h1: t('saleCalendar:head.title', { country }),
      description: sentences([
        events.length
          ? t('saleCalendar:head.summaryEvents', { country, events: eventDates })
          : t('saleCalendar:head.summary', { country }),
      ]),
      path,
      robots: INDEX_ROBOTS,
      breadcrumbs: [home, { name: t('common:crumb.saleCalendar'), path }],
    },
    options,
  );
}

// ─── Legal and other fixed pages ───────────────────────────────────────────

/**
 * A fixed page with its own title and description (legal pages). A fixed
 * title that already names the site ("Privacy Policy — PromoStork Website")
 * is used as written.
 */
export function fixedPageHead(
  input: { title: string; description: string; path: string; noindex?: boolean },
  options: HeadOptions = {},
): SeoHead {
  const namesSite = input.title.toLowerCase().includes(SITE_NAME.toLowerCase());
  return buildHead(
    {
      title: namesSite ? input.title : withSiteName(input.title, undefined, ctx(options).t),
      h1: input.title,
      description: input.description,
      path: input.path,
      robots: robotsValue({ noindex: input.noindex }),
      breadcrumbs: [],
    },
    options,
  );
}

// ─── Pages titled by name (auth, account, search, 404, …) ─────────────────

/**
 * "{title} · PromoStork", with " – Page N" before the site name for page
 * N ≥ 2: the title `<PageHead title page>` renders.
 */
export function titledPageTitle(title: string, page?: number | null, t: Translate = defaultT): string {
  const n = validPage(page ?? undefined);
  const core = title.trim();
  return `${n ? t('common:title.page', { title: core, page: n }) : core}${TITLED_SEPARATOR}${SITE_NAME}`;
}

/**
 * The head a page renders with `<PageHead title description noindex page
 * jsonLd />` and no head of its own (sign-in, account and legal pages without
 * a prerender, /search, /shops, the not-found page): its title, the
 * description or the site's, a canonical of `path` (`?page=N` kept for
 * N ≥ 2), index or `noindex, follow`, and the site's share image. The SEO
 * server sends it before JavaScript runs (web plan W1.3), so it must stay
 * exactly what `<PageHead/>` renders for the same options (src/App.serverHeads.test.tsx).
 */
export function titledHead(
  input: {
    title: string;
    description?: string | null;
    path: string;
    noindex?: boolean;
    page?: number | null;
    jsonLd?: object | object[] | null;
  },
  options: Pick<HeadOptions, 'locale' | 't'> = {},
): SeoHead {
  const { locale, t } = ctx(options);
  const n = validPage(input.page ?? undefined);
  const title = titledPageTitle(input.title, n, t);
  // The site's description (DEFAULT_DESCRIPTION in DEFAULT_LOCALE) in `t`'s language.
  const description = pagedDescription(
    clampText(
      (input.description ?? '').replace(/\s+/g, ' ').trim() || homeDescription(t, locale.country, false),
      DESCRIPTION_MAX,
    ),
    n,
    t,
  );
  let canonicalPath = canonicalFor(input.path);
  if (n && !canonicalPath.includes('?')) canonicalPath = `${canonicalPath}?page=${n}`;
  const canonical = absoluteUrl(canonicalPath);
  const image = absoluteUrl(DEFAULT_OG_IMAGE_PATH);
  const imageAlt = siteImageAlt(t);
  return {
    title,
    h1: input.title.trim(),
    description,
    canonicalPath,
    canonical,
    robots: robotsValue({ noindex: input.noindex }),
    status: 200,
    htmlLang: locale.hreflang,
    ...(localeDir(locale) === 'rtl' ? { htmlDir: 'rtl' as const } : {}),
    og: {
      type: 'website',
      siteName: SITE_NAME,
      locale: ogLocale(locale),
      title,
      description,
      url: canonical,
      image,
      imageAlt,
      imageSize: ogImageSize(image),
    },
    twitter: { card: 'summary_large_image', title, description, image, imageAlt },
    breadcrumbs: [],
    jsonLd: input.jsonLd ? ([] as object[]).concat(input.jsonLd) : [],
  };
}

// ─── Alt text ──────────────────────────────────────────────────────────────

/** Decorative images and map tiles. */
export const DECORATIVE_ALT = '';

/** "{Brand} logo" (empty when the name is: the image is then decorative). */
export function brandLogoAlt(brandName: string | null | undefined, t: Translate = defaultT): string {
  const brand = cleanName(brandName);
  return brand ? t('common:alt.logo', { name: brand }) : DECORATIVE_ALT;
}

/** "{Brand} cover image". */
export function brandCoverAlt(brandName: string | null | undefined, t: Translate = defaultT): string {
  const brand = cleanName(brandName);
  return brand ? t('common:alt.cover', { name: brand }) : DECORATIVE_ALT;
}

/** "{Brand} {Clean headline}", without repeating the brand. */
function promotionLabel(input: { brandName?: string | null; headline?: string | null }, t: Translate): string {
  const brand = cleanName(input.brandName);
  const headline = cleanName(input.headline);
  if (!headline) return brand;
  return brand && !startsWithWord(headline, brand) ? t('common:alt.label', { brand, headline }) : headline;
}

/** "{Brand} {Clean headline} poster", plus " (page {n} of {N})" for a multi-page booklet. */
export function promotionPosterAlt(
  input: {
    brandName?: string | null;
    headline?: string | null;
    page?: number;
    pageCount?: number;
  },
  t: Translate = defaultT,
): string {
  const label = promotionLabel(input, t);
  if (!label) return DECORATIVE_ALT;
  const { page, pageCount } = input;
  return pageCount && pageCount > 1 && page && page >= 1 && page <= pageCount
    ? t('common:alt.posterPage', { label, page, pageCount })
    : t('common:alt.poster', { label });
}

/** "{Brand} {Clean headline} video". */
export function promotionVideoAlt(
  input: { brandName?: string | null; headline?: string | null },
  t: Translate = defaultT,
): string {
  const label = promotionLabel(input, t);
  return label ? t('common:alt.video', { label }) : DECORATIVE_ALT;
}

/** A store row reusing the brand logo: "{Brand} {Area}, {City}". */
export function storeImageAlt(input: { brandName?: string | null; area?: string | null; city?: string | null }): string {
  const brand = cleanName(input.brandName);
  const place = [cleanName(input.area), cleanName(input.city)].filter(Boolean).join(', ');
  return [brand, place].filter(Boolean).join(' ');
}

// ─── Static heads (plan W0.18) ─────────────────────────────────────────────

/** One fixed route in `staticHeads.json`. */
export interface StaticHead {
  path: string;
  title: string;
  h1: string;
  description: string;
  robots: string;
  /**
   * Whether the prerendered file may carry an absolute self-canonical and
   * `og:url`: only for routes that never take a query. `/brands` and
   * `/promotions` are also served for `?page=N`, so theirs is set in the
   * browser instead.
   */
  selfCanonical: boolean;
  /**
   * Share image: a file under `public/` (`/og-image.jpg`, the site image,
   * unless the route has its own). The prerender puts it on the build's
   * origin, so it is a path, never a URL.
   */
  image: string;
  /** Alt text of `image`: "PromoStork logo" for the site image, else the title. */
  imageAlt: string;
  /**
   * `<html lang>` (`en-BH`) and `og:locale` (`en_BH`) of a route outside the
   * default locale; left out for the default locale's routes, whose files keep
   * index.html's own (`en-PK`).
   */
  htmlLang?: string;
  ogLocale?: string;
}

/**
 * The prerendered legal pages' titles and descriptions: the one source for the
 * page (its component passes them to `LegalLayout`, which renders the H1 and
 * builds the head with `fixedPageHead`) and for its prerendered file.
 */
export const PRIVACY_PAGE = Object.freeze({
  path: '/privacy',
  title: defaultT('legal:privacy.title', { site: SITE_NAME }),
  description: defaultT('legal:privacy.description', { site: SITE_NAME }),
});

export const TERMS_PAGE = Object.freeze({
  path: '/terms',
  title: defaultT('legal:terms.title', { site: SITE_NAME }),
  description: defaultT('legal:terms.description', { site: SITE_NAME }),
});

export const LEGAL_PAGES = [PRIVACY_PAGE, TERMS_PAGE] as const;

function toStatic(head: SeoHead, selfCanonical: boolean, locale: Locale = DEFAULT_LOCALE): StaticHead {
  const origin = `${SITE_URL}/`;
  if (!head.og.image.startsWith(origin)) {
    throw new RangeError(
      `buildStaticHeads: ${head.canonicalPath} uses ${head.og.image}; a prerendered head's image must be a file under public/`,
    );
  }
  return {
    path: head.canonicalPath,
    title: head.title,
    h1: head.h1,
    description: head.description,
    robots: head.robots,
    selfCanonical,
    image: head.og.image.slice(SITE_URL.length),
    imageAlt: head.og.imageAlt,
    ...(locale.cc === DEFAULT_LOCALE.cc ? {} : { htmlLang: head.htmlLang, ogLocale: head.og.locale }),
  };
}

/**
 * A fixed route's complete head, from its `staticHeads.json` entry: exactly
 * what its prerendered file carries (title, description, robots, card). The
 * page's `<PageHead/>` renders it on that route when the page brings no head of its
 * own, so the head never changes when JavaScript takes over.
 */
export function staticRouteHead(route: StaticHead, options: HeadOptions = {}): SeoHead {
  const ownImage = route.image !== DEFAULT_OG_IMAGE_PATH;
  return buildHead(
    {
      title: route.title,
      h1: route.h1,
      description: route.description,
      path: route.path,
      robots: route.robots,
      breadcrumbs: [],
    },
    { ...options, image: ownImage ? absoluteUrl(route.image) : null, imageAlt: route.imageAlt },
  );
}

/**
 * The fixed routes and their heads, exactly as `staticHeads.json` must hold
 * them (a unit test compares the two). Data-free by design: the list pages use
 * their "no live offers" variants, because a prerendered file cannot know what
 * is live when it is served.
 */
export function buildStaticHeads(
  events: readonly SaleEvent[],
  locale: Locale = DEFAULT_LOCALE,
  t: Translate = tFor(locale),
): StaticHead[] {
  const options: HeadOptions = { locale, t };
  if (locale.cc !== DEFAULT_LOCALE.cc) {
    // Another country's fixed catalog routes, in its own words (web plan MW3).
    // No sale pages (another country's events are data: the SEO server answers them from the
    // backend, `saleCountries.ts`) and no legal pages
    // (global, un-prefixed); /search is noindex, so it has no prerendered head.
    return [
      toStatic(brandsListHead({}, options), false, locale),
      toStatic(promotionsListHead({ liveOffers: false }, options), false, locale),
      toStatic(shopsListHead({}, options), false, locale),
      toStatic(nearbyHead(options), true, locale),
    ];
  }
  return [
    toStatic(brandsListHead({}, options), false),
    toStatic(promotionsListHead({ liveOffers: false }, options), false),
    toStatic(saleCalendarHead(events, options), true),
    ...events.map((event) => toStatic(saleEventHead(event, options), true)),
    ...LEGAL_PAGES.map((page) => toStatic(fixedPageHead(page, options), true)),
  ];
}

/**
 * Every launched locale's fixed routes: the default locale's (Pakistan's
 * output is unchanged), then each other locale's, in `LOCALES` order. What
 * `staticHeads.json` holds.
 */
export function buildAllStaticHeads(events: readonly SaleEvent[]): StaticHead[] {
  return [
    ...buildStaticHeads(events, DEFAULT_LOCALE),
    ...LOCALES.filter((locale) => locale.cc !== DEFAULT_LOCALE.cc).flatMap((locale) => buildStaticHeads(events, locale)),
  ];
}
